import { useLocation, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface Provider {
  id: number;
  name: string;
}

interface Version {
  id: number;
  version_number: string;
  region_id?: number;
}

interface Region {
  id: number;
  name: string;
  zip_code?: string;
}

export function Breadcrumb() {
  const location = useLocation();
  const paths = location.pathname.split('/').filter(Boolean);

  // Extract IDs from path
  const providerId = paths[1];
  const isRegionRoute = paths[2] === 'regions';
  const regionId = isRegionRoute ? paths[3] : undefined;
  const versionId = isRegionRoute ? paths[5] : paths[3];

  // Fetch provider name if ID exists
  const { data: provider } = useQuery<Provider | null>({
    queryKey: ['breadcrumb-provider', providerId] as const,
    queryFn: async () => {
      if (!/^\d+$/.test(providerId)) return null;
      const { data, error } = await supabase
        .from('providers')
        .select('id, name')
        .eq('id', parseInt(providerId))
        .single();
      if (error) throw error;
      return data as Provider;
    },
    enabled: !!providerId && /^\d+$/.test(providerId)
  });

  // Fetch version details if ID exists, including region_id
  const { data: version } = useQuery<Version | null>({
    queryKey: ['breadcrumb-version', versionId] as const,
    queryFn: async () => {
      if (!/^\d+$/.test(versionId || '')) return null;
      const { data, error } = await supabase
        .from('provider_versions')
        .select('id, version_number, region_id')
        .eq('id', parseInt(versionId!))
        .single();
      if (error) throw error;
      return data as Version;
    },
    enabled: !!versionId && /^\d+$/.test(versionId || '')
  });

  // Get region ID either from URL or from version data
  const effectiveRegionId = regionId || (version?.region_id?.toString());

  // Fetch region name if ID exists (from either URL or version)
  const { data: region } = useQuery<Region | null>({
    queryKey: ['breadcrumb-region', effectiveRegionId] as const,
    queryFn: async () => {
      if (!/^\d+$/.test(effectiveRegionId || '')) return null;
      const { data, error } = await supabase
        .from('regions')
        .select('id, name, zip_code')
        .eq('id', parseInt(effectiveRegionId!))
        .single();
      if (error) throw error;
      return data as Region;
    },
    enabled: !!effectiveRegionId && /^\d+$/.test(effectiveRegionId)
  });

  // Don't render if we're at the root path
  if (paths.length === 0) return null;

  // Build breadcrumb paths
  const breadcrumbPaths = [...paths];
  
  // For version dashboards with region context, insert region into breadcrumb
  if (!isRegionRoute && version?.region_id && region && 
     (paths.includes('dashboard') || paths.includes('channels') || 
      paths.includes('plans') || paths.includes('categories'))) {
    // Add region to breadcrumb paths at the right position
    breadcrumbPaths.splice(2, 0, 'regions', region.id.toString());
  }

  return (
    <nav className="mb-4" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        <li>
          <Link
            to="/"
            className="text-sm font-medium hover:text-primary"
          >
            Home
          </Link>
        </li>
        {breadcrumbPaths.map((path, index) => {
          let displayPath = path;
          
          // Skip rendering if this is an ID and we have the actual name/number
          if (path === providerId && provider) {
            displayPath = provider.name;
          }
          
          // Handle region display
          if ((path === regionId || path === region?.id?.toString()) && region) {
            displayPath = region.name;
            if (region.zip_code) {
              displayPath += ` (${region.zip_code})`;
            }
          }
          
          if (path === versionId && version) {
            displayPath = `Version ${version.version_number}`;
          }

          // Handle path display text for common sections
          if (path === 'dashboard') displayPath = 'Dashboard';
          if (path === 'regions') displayPath = 'Regions';
          if (path === 'versions') displayPath = 'Versions';

          // Build the correct path
          let currentPath = breadcrumbPaths.slice(0, index + 1).join('/');
          
          // Special handling for region navigation
          if ((path === regionId || path === region?.id?.toString()) && 
              (breadcrumbPaths[index-1] === 'regions')) {
            currentPath = `providers/${providerId}/regions`;
          }
          
          return (
            <li key={`${path}-${index}`} className="flex items-center">
              <ChevronRight className="mx-2 h-4 w-4 text-muted-foreground" />
              <Link
                to={`/${currentPath}`}
                className="text-sm font-medium hover:text-primary"
              >
                {formatBreadcrumb(displayPath)}
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function formatBreadcrumb(path: string): string {
  // If the path is already formatted (like a provider name or version number), return as is
  if (path.includes(' ') || /^Version \d+\.\d+$/.test(path)) return path;
  
  return path
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}